import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  IconButton,
  Alert,
  MenuItem,
  InputAdornment,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { Close, Save, Cancel, AccountBalance } from "@mui/icons-material";

const GenericModal = ({
  open,
  onClose,
  onSave,
  data,
  title,
  fields,
  initialForm,
  validateForm: customValidateForm,
  mapFormValues,
  optionData = [],
  dispatch,
  saveButtonText,
  updateButtonText,
  width = 180,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && data) {
        setForm({ ...initialForm, ...data });
    } else {
      setForm(initialForm);
    }
  }, [open, data, initialForm, optionData]);

  const validateForm = () => {
    if (customValidateForm) {
      const validationResult = customValidateForm(form);
      setErrors(validationResult);
      return Object.keys(validationResult).length === 0;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setForm((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      let formData = { ...form };
      
      // Apply custom mapping if provided
      if (mapFormValues) {
        formData = mapFormValues(formData, optionData);
      }
      
      await onSave(formData);
      handleClose();
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setForm(initialForm);
    setErrors({});
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? "100vh" : 500,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(90deg, #22223b 0%, #4a4e69 100%)"
              : "linear-gradient(90deg, #6a11cb 0%, #2575fc 100%)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalance fontSize="medium" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {data ? `Edit ${title}` : `Add ${title}`}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: "white" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 4 }, py: 3, display: "grid" }}>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">
            {data
              ? `Please review and update the required fields for this ${title.toLowerCase()}.`
              : `Please fill in all required fields to add a new ${title.toLowerCase()}.`}
          </Alert>
        </Box>

        <Grid container spacing={1}>
          {fields.map((field) => (
            <Grid item size={{ xs: field.size?.xs ?? 12, sm: field.size?.sm ?? 12, md: field.size?.md ?? 6}} key={field.name}>
                {field.type === "select" ? (
                  <TextField
                    name={field.name}
                    label={field.label}
                    size="small"
                    select
                    fullWidth
                    value={form[field.name] !== undefined ? form[field.name] : ""}
                    onChange={(e) => {
                      handleChange(e);
                      field.onChange?.(e, { form, setForm, optionData, dispatch });
                    }}
                    onBlur={(e) => {
                      field.onBlur?.(e, { form, setForm, optionData, dispatch, field });
                    }}
                    error={!!errors[field.name]}
                    helperText={errors[field.name] || field.helper}
                    disabled={field.disabled}
                    sx={{ backgroundColor: field.backgroundColor }}
                  >
                    {(field.options ||
                      (optionData.find((opt) => opt.optionName === field.optionName)?.values || [])
                    ).map((opt) => (
                      <MenuItem key={opt.id || opt.optionValue} value={opt.optionValue}>
                        {opt.optionText}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : field.type === "checkbox" ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form[field.name] ?? false}
                        onChange={handleChange}
                        name={field.name}
                        color="primary"
                        disabled={field.disabled}
                        sx={{ backgroundColor: field.backgroundColor }}
                      />
                    }
                    label={field.label}
                  />
                ) : (
                  <TextField
                    name={field.name}
                    label={field.label}
                    size="small"
                    type={field.type || "text"}
                    fullWidth
                    value={form[field.name] !== undefined ? form[field.name] : ""}
                    multiline={field.multiline}
                    rows={field.rows}
                    error={!!errors[field.name]}
                    onChange={(e) => {
                      handleChange(e);
                      field.onChange?.(e, { setForm, dispatch, field });
                    }}
                    onBlur={(e) => {
                      field.onBlur?.(e, { form, setForm, optionData, dispatch, field });
                    }}
                    helperText={errors[field.name] ||
                      (typeof field.helper === "function"
                        ? <b>{field.helper(form)}</b>
                        : field.helper)}
                    disabled={field.disabled}
                    sx={{ backgroundColor: field.backgroundColor }}
                    InputLabelProps={{
                        shrink: (field.type === "date" || field.type === "datetime-local" || field.type === "time") ? true : undefined
                    }}
                    InputProps={{
                      startAdornment: field.adornment && (
                        <InputAdornment position="start">
                          {typeof field.adornment === "function"
                            ? field.adornment(form)
                            : field.adornment}
                        </InputAdornment>
                      ),
                      endAdornment: field.endAdornment && (
                        <InputAdornment position="end">
                          {typeof field.endAdornment === "function"
                            ? field.endAdornment(form)
                            : field.endAdornment}
                        </InputAdornment>
                      ),
                      inputProps: { min: field.min || 0, readOnly: field.readOnly || false },
                    }}
                  />
                )}
              </Grid>
          ))}

          {/* Switches */}
          {Object.hasOwn(form, "active") && (
            <Grid item size={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.active ?? true}
                    onChange={handleChange}
                    name="active"
                    color="success"
                  />
                }
                label="Active"
              />
            </Grid>
          )}

          {Object.hasOwn(form, "editable") && (
            <Grid item size={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.editable ?? true}
                    onChange={handleChange}
                    name="editable"
                    color="primary"
                  />
                }
                label="Editable (Allow modification)"
              />
            </Grid>
          )}

        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
        <Button
          onClick={handleClose}
          startIcon={<Cancel />}
          color="error"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress size={16} /> : <Save />}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading
            ? "Please wait saving..."
            : data
            ? updateButtonText || `Update ${title}`
            : saveButtonText || `Save ${title}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenericModal;